import React, { useEffect, useRef, useState } from 'react';
import {
  cancelPlatformSupportSeatPurchase,
  clearPlatformSupportSession,
  changePlatformSupportLicenseLifecycle,
  changePlatformSupportSeatPurchase,
  createPlatformSupportSeatCapacityPlan,
  fetchPlatformSupportLicenses,
  fetchPlatformSupportLicenseTargets,
  fetchPlatformSupportSession,
  loadPlatformSupportSession,
  loginPlatformSupport,
  logoutPlatformSupport,
  issuePlatformSupportLicense,
  savePlatformSupportSession,
  type IssuedLicenseResult,
  type PlatformSupportLicense,
  type PlatformSupportLicenseList,
  type PlatformSupportLicenseTarget,
  type PlatformSupportLicenseTargets,
  type PlatformSupportSession
} from '../services/platformSupportAuth';
import './PlatformSupportConsole.css';
import PlatformSupportTicketsWorkspace from './PlatformSupportTicketsWorkspace';
import PlatformSupportUserActivityWorkspace from './PlatformSupportUserActivityWorkspace';
import PlatformSupportAlertsWorkspace from './PlatformSupportAlertsWorkspace';

function roleLabel(role: PlatformSupportSession['user']['role']) {
  return role === 'SUPPORT_ADMIN' ? 'Support Admin' : 'Support Agent';
}

type SupportModule = 'overview' | 'licensing' | 'tickets' | 'activity' | 'alerts';

function formatDate(value: string | null | undefined) {
  if (!value) return 'Not set';
  return new Intl.DateTimeFormat(undefined, { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(value));
}

function healthLabel(health: string) {
  return health.replace(/^EXPIRY_/, 'Expiry ').replaceAll('_', ' ').toLowerCase().replace(/\b\w/g, letter => letter.toUpperCase());
}

function seatPurchaseLifecycle(entitlement: PlatformSupportLicense['entitlements'][number]) {
  return entitlement.lifecycleState;
}

const PlatformSupportConsole: React.FC = () => {
  const initialSessionRef = useRef<PlatformSupportSession | null>(loadPlatformSupportSession());
  const [session, setSession] = useState<PlatformSupportSession | null>(initialSessionRef.current);
  const [checkingSession, setCheckingSession] = useState(Boolean(session));
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [module, setModule] = useState<SupportModule>('overview');
  const [licenseData, setLicenseData] = useState<PlatformSupportLicenseList | null>(null);
  const [licensingBusy, setLicensingBusy] = useState(false);
  const [licenseSearch, setLicenseSearch] = useState('');
  const [licenseStatus, setLicenseStatus] = useState('ALL');
  const [licenseScope, setLicenseScope] = useState('ALL');
  const [licensePage, setLicensePage] = useState(1);
  const [expandedLicenseId, setExpandedLicenseId] = useState<string | null>(null);
  const [licenseRefreshKey, setLicenseRefreshKey] = useState(0);

  useEffect(() => {
    if (!session?.token || module !== 'licensing') return;
    const timer = window.setTimeout(() => {
      setLicensingBusy(true);
      setError(null);
      void fetchPlatformSupportLicenses(session.token, {
        page: licensePage,
        pageSize: 25,
        search: licenseSearch.trim(),
        status: licenseStatus,
        scopeType: licenseScope
      })
        .then(setLicenseData)
        .catch(err => setError(err instanceof Error ? err.message : 'Platform licensing records could not be loaded'))
        .finally(() => setLicensingBusy(false));
    }, licenseSearch ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [session, module, licenseSearch, licenseStatus, licenseScope, licensePage, licenseRefreshKey]);

  useEffect(() => {
    const storedSession = initialSessionRef.current;
    if (!storedSession?.token) {
      setCheckingSession(false);
      return;
    }
    void fetchPlatformSupportSession(storedSession.token)
      .then(user => {
        const hydrated = { ...storedSession, user };
        savePlatformSupportSession(hydrated);
        setSession(hydrated);
      })
      .catch(() => {
        clearPlatformSupportSession();
        setSession(null);
        setLicenseData(null);
      })
      .finally(() => setCheckingSession(false));
  }, []); // hydrate only the initially stored support session

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const next = await loginPlatformSupport(username.trim().toLowerCase(), password);
      savePlatformSupportSession(next);
      setSession(next);
      setModule('overview');
      setLicenseData(null);
      setPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Platform support sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  const signOut = async () => {
    if (!session?.token) return;
    setBusy(true);
    setError(null);
    try {
      await logoutPlatformSupport(session.token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Platform support sign-out audit failed');
    }
    clearPlatformSupportSession();
    setSession(null);
    setModule('overview');
    setLicenseData(null);
    setExpandedLicenseId(null);
    setBusy(false);
  };

  if (checkingSession) {
    return <div style={styles.loading}>Checking BonzaiQ support session…</div>;
  }

  if (!session) {
    return (
      <main style={styles.loginPage}>
        <form style={styles.loginCard} onSubmit={submit}>
          <div style={styles.brandMark}>bonzaiQ</div>
          <div style={styles.eyebrow}>BONZAIQ INTERNAL</div>
          <h1 style={styles.loginTitle}>Platform Support</h1>
          <p style={styles.loginText}>Sign in with a BonzaiQ support account. Customer software accounts cannot access this console.</p>
          <label style={styles.label} htmlFor="platform-support-username">Username</label>
          <input
            id="platform-support-username"
            style={styles.input}
            value={username}
            onChange={event => setUsername(event.target.value)}
            autoComplete="username"
            required
          />
          <label style={styles.label} htmlFor="platform-support-password">Password</label>
          <div style={styles.passwordRow}>
            <input
              id="platform-support-password"
              style={{ ...styles.input, flex: 1 }}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={event => setPassword(event.target.value)}
              autoComplete="current-password"
              required
            />
            <button type="button" style={styles.secondaryButton} onClick={() => setShowPassword(value => !value)}>
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          {error && <div style={styles.error}>{error}</div>}
          <button type="submit" style={styles.primaryButton} disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in to Support'}
          </button>
          <a href="#/portal" style={styles.productLink}>Return to the BonzaiQ product</a>
        </form>
      </main>
    );
  }

  return (
    <div className="platform-support-console" style={styles.consoleShell}>
      <header style={styles.header}>
        <div>
          <div style={styles.headerBrand}>bonzaiQ</div>
          <div style={styles.headerProduct}>Platform Support</div>
        </div>
        <div style={styles.headerAccount}>
          <div>
            <strong>{session.user.fullName || session.user.username}</strong>
            <span style={styles.accountRole}>{roleLabel(session.user.role)}</span>
          </div>
          <button type="button" style={styles.headerButton} onClick={signOut} disabled={busy}>Sign out</button>
        </div>
      </header>
      <div className="platform-support-workspace" style={styles.workspace}>
        <nav className="platform-support-navigation" style={styles.navigation} aria-label="Platform Support modules">
          <button type="button" style={{ ...styles.navButton, ...(module === 'overview' ? styles.navItemActive : {}) }} onClick={() => setModule('overview')}>Overview</button>
          <button type="button" style={{ ...styles.navButton, ...(module === 'licensing' ? styles.navItemActive : {}) }} onClick={() => setModule('licensing')}>Licensing <span style={styles.liveChip}>{session.user.role === 'SUPPORT_ADMIN' ? 'Admin' : 'Read only'}</span></button>
          <button type="button" style={{ ...styles.navButton, ...(module === 'tickets' ? styles.navItemActive : {}) }} onClick={() => setModule('tickets')}>Support tickets</button>
          <button type="button" style={{ ...styles.navButton, ...(module === 'activity' ? styles.navItemActive : {}) }} onClick={() => setModule('activity')}>User activity</button>
          <button type="button" style={{ ...styles.navButton, ...(module === 'alerts' ? styles.navItemActive : {}) }} onClick={() => setModule('alerts')}>Proactive alerts</button>
        </nav>
        <main className="platform-support-content" style={styles.content}>
          {error && <div style={styles.error}>{error}</div>}
          {module === 'overview' ? <>
            <div style={styles.pageHeading}>
            <div>
              <div style={styles.eyebrow}>BONZAIQ OPERATIONS</div>
              <h1 style={styles.pageTitle}>Support workspace</h1>
              <p style={styles.pageText}>A separate workspace for BonzaiQ licensing and future customer-support operations.</p>
            </div>
            <span style={styles.realmChip}>Support identity</span>
          </div>
          <section style={styles.boundaryCard}>
            <div style={styles.boundaryIcon}>✓</div>
            <div>
              <h2 style={styles.cardTitle}>Platform boundary active</h2>
              <p style={styles.cardText}>This session belongs to BonzaiQ support. It is not a customer UNIT or ORG account and consumes no product licence seat.</p>
            </div>
          </section>
          <div style={styles.moduleGrid}>
            <section style={styles.moduleCard}>
              <div style={styles.moduleLabel}>CURRENT MODULE</div>
              <h2 style={styles.cardTitle}>Licensing administration</h2>
              <p style={styles.cardText}>Issue activation keys, manage terms and seat quantities, and review licence health across customer accounts.</p>
            </section>
            <section style={styles.moduleCard}>
              <div style={styles.moduleLabel}>CURRENT MODULE</div>
              <h2 style={styles.cardTitle}>Support tickets</h2>
              <p style={styles.cardText}>Receive, assign and resolve customer-submitted support requests without combining customer and BonzaiQ identities.</p>
            </section>
            <section style={styles.moduleCard}>
              <div style={styles.moduleLabel}>CURRENT MODULE</div>
              <h2 style={styles.cardTitle}>User activity</h2>
              <p style={styles.cardText}>Review login sessions, active-use duration, approximate network context, and allow-listed page visits.</p>
            </section>
            <section style={styles.moduleCard}>
              <div style={styles.moduleLabel}>CURRENT MODULE</div>
              <h2 style={styles.cardTitle}>Proactive alerts</h2>
              <p style={styles.cardText}>Review dormant access, inactive accounts, login failures, licence renewal, and seat-capacity risks.</p>
            </section>
          </div>
          </> : module === 'tickets' ? <PlatformSupportTicketsWorkspace token={session.token} /> : module === 'activity' ? <PlatformSupportUserActivityWorkspace token={session.token} /> : module === 'alerts' ? <PlatformSupportAlertsWorkspace
            token={session.token}
            onReviewLicensing={accountName => { setLicenseSearch(accountName); setLicensePage(1); setModule('licensing'); }}
            onReviewActivity={() => setModule('activity')}
          /> : <LicensingWorkspace
            token={session.token}
            canIssue={session.user.role === 'SUPPORT_ADMIN'}
            data={licenseData}
            busy={licensingBusy}
            search={licenseSearch}
            status={licenseStatus}
            scope={licenseScope}
            expandedLicenseId={expandedLicenseId}
            onSearch={value => { setLicenseSearch(value); setLicensePage(1); }}
            onStatus={value => { setLicenseStatus(value); setLicensePage(1); }}
            onScope={value => { setLicenseScope(value); setLicensePage(1); }}
            onToggleLicense={id => setExpandedLicenseId(current => current === id ? null : id)}
            onPage={setLicensePage}
            onIssued={() => { setLicensePage(1); setLicenseRefreshKey(value => value + 1); }}
          />}
        </main>
      </div>
    </div>
  );
};

type LicensingWorkspaceProps = {
  token: string;
  canIssue: boolean;
  data: PlatformSupportLicenseList | null;
  busy: boolean;
  search: string;
  status: string;
  scope: string;
  expandedLicenseId: string | null;
  onSearch: (value: string) => void;
  onStatus: (value: string) => void;
  onScope: (value: string) => void;
  onToggleLicense: (id: string) => void;
  onPage: (page: number) => void;
  onIssued: () => void;
};

const LicensingWorkspace: React.FC<LicensingWorkspaceProps> = ({ token, canIssue, data, busy, search, status, scope, expandedLicenseId, onSearch, onStatus, onScope, onToggleLicense, onPage, onIssued }) => {
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [capacityPlan, setCapacityPlan] = useState<{ license: PlatformSupportLicense; productCode?: string } | null>(null);
  const [managedPool, setManagedPool] = useState<{ license: PlatformSupportLicense; pool: SeatPool } | null>(null);
  const [managedPurchase, setManagedPurchase] = useState<{ license: PlatformSupportLicense; entitlement: PlatformSupportLicense['entitlements'][number] } | null>(null);
  const [lifecycleChange, setLifecycleChange] = useState<{ license: PlatformSupportLicense; action: LicenseLifecycleAction } | null>(null);
  return <>
    <div style={styles.pageHeading}>
      <div>
        <div style={styles.eyebrow}>BONZAIQ LICENSING</div>
        <h1 style={styles.pageTitle}>Customer licences</h1>
        <p style={styles.pageText}>Review licence health, seat usage and activation status across UNIT and ORG accounts.</p>
      </div>
      {canIssue
        ? <button type="button" style={styles.issueButton} onClick={() => setShowIssueModal(true)}>Issue licence</button>
        : <span style={styles.readOnlyChip}>Read only</span>}
    </div>
    <div className="platform-support-summary" style={styles.summaryGrid}>
      <SummaryMetric label="Current user seats" value={data?.summary.purchasedSeats ?? 0} />
      <SummaryMetric label="Assigned user seats" value={data?.summary.assignedSeats ?? 0} />
      <SummaryMetric label="All licences" value={data?.summary.totalLicenses ?? '—'} />
      <SummaryMetric label="Active" value={data?.summary.activeLicenses ?? '—'} />
      <SummaryMetric label="Need attention on this page" value={data?.summary.attentionOnPage ?? '—'} tone={(data?.summary.attentionOnPage || 0) > 0 ? 'warning' : 'normal'} />
    </div>
    <section className="platform-support-license-filters" style={styles.filterCard}>
      <label style={styles.filterLabel}>Search
        <input style={styles.filterInput} value={search} onChange={event => onSearch(event.target.value)} placeholder="Licence, customer, UNIT or ORG" />
      </label>
      <label style={styles.filterLabel}>Status
        <select style={styles.filterInput} value={status} onChange={event => onStatus(event.target.value)}>
          <option value="ALL">All statuses</option>
          {['ACTIVE', 'ISSUED', 'DRAFT', 'SUSPENDED', 'EXPIRED', 'REVOKED'].map(value => <option key={value} value={value}>{healthLabel(value)}</option>)}
        </select>
      </label>
      <label style={styles.filterLabel}>Scope
        <select style={styles.filterInput} value={scope} onChange={event => onScope(event.target.value)}>
          <option value="ALL">UNIT and ORG</option>
          <option value="UNIT">UNIT</option>
          <option value="GROUP">ORG</option>
        </select>
      </label>
    </section>
    <section style={styles.licenseListCard} aria-busy={busy}>
      <div style={styles.listHeading}>
        <div><strong>{data?.pagination.total ?? 0} licences</strong><span style={styles.mutedText}>Select a licence to review its safe operational summary.</span></div>
        {busy && <span style={styles.loadingChip}>Refreshing…</span>}
      </div>
      {!busy && data?.records.length === 0 && <div style={styles.emptyState}>No licences match these filters.</div>}
      <div style={styles.licenseList}>
        {data?.records.map(license => <LicenseRow
          key={license.id}
          license={license}
          expanded={expandedLicenseId === license.id}
          canManage={canIssue}
          graceDays={data.graceDays}
          onToggle={() => onToggleLicense(license.id)}
          onAddCapacity={productCode => setCapacityPlan({ license, productCode })}
          onManagePeriods={pool => setManagedPool({ license, pool })}
          onLifecycle={action => setLifecycleChange({ license, action })}
        />)}
      </div>
      {data && data.pagination.totalPages > 1 && <div style={styles.pagination}>
        <button type="button" style={styles.secondaryButton} disabled={data.pagination.page <= 1 || busy} onClick={() => onPage(data.pagination.page - 1)}>Previous</button>
        <span>Page {data.pagination.page} of {data.pagination.totalPages}</span>
        <button type="button" style={styles.secondaryButton} disabled={data.pagination.page >= data.pagination.totalPages || busy} onClick={() => onPage(data.pagination.page + 1)}>Next</button>
      </div>}
    </section>
    {showIssueModal && <IssueLicenseModal token={token} onClose={() => setShowIssueModal(false)} onIssued={onIssued} />}
    {capacityPlan && <CapacityPlanModal token={token} license={capacityPlan.license} initialProductCode={capacityPlan.productCode} onClose={() => setCapacityPlan(null)} onSaved={() => { setCapacityPlan(null); onIssued(); }} />}
    {managedPool && <SeatPeriodsModal license={managedPool.license} pool={managedPool.pool} canManage={canIssue} onClose={() => setManagedPool(null)} onAdd={() => { setCapacityPlan({ license: managedPool.license, productCode: managedPool.pool.product.code }); setManagedPool(null); }} onManage={entitlement => { setManagedPurchase({ license: managedPool.license, entitlement }); setManagedPool(null); }} />}
    {managedPurchase && <ManageSeatPurchaseModal token={token} license={managedPurchase.license} entitlement={managedPurchase.entitlement} graceDays={data?.graceDays || 14} onClose={() => setManagedPurchase(null)} onSaved={() => { setManagedPurchase(null); onIssued(); }} />}
    {lifecycleChange && <LicenseLifecycleModal token={token} license={lifecycleChange.license} action={lifecycleChange.action} onClose={() => setLifecycleChange(null)} onSaved={() => { setLifecycleChange(null); onIssued(); }} />}
  </>
  ;
};

const UNIT_SEAT_INPUTS = [
  ['UNIT_ADMIN', 'Unit Admin'],
  ['UNIT_SUPERUSER', 'Unit Super User'],
  ['UNIT_TECH', 'Unit Technician'],
  ['UNIT_WAREHOUSE', 'Unit Warehouse User'],
  ['UNIT_DEVICE_EXPERT', 'Unit Device Expert']
] as const;

const ORG_SEAT_INPUTS = [
  ['ORG_ADMIN', 'ORG Admin'],
  ['ORG_ANALYST', 'ORG Analyst']
] as const;

const SEAT_LABELS: Record<string, string> = Object.fromEntries([...UNIT_SEAT_INPUTS, ...ORG_SEAT_INPUTS]);

const IssueLicenseModal: React.FC<{ token: string; onClose: () => void; onIssued: () => void }> = ({ token, onClose, onIssued }) => {
  const [customerName, setCustomerName] = useState('');
  const [targetMode, setTargetMode] = useState<'NEW_UNIT' | 'EXISTING_UNIT' | 'EXISTING_GROUP'>('NEW_UNIT');
  const [targetId, setTargetId] = useState('');
  const [targets, setTargets] = useState<PlatformSupportLicenseTargets | null>(null);
  const [targetsLoading, setTargetsLoading] = useState(true);
  const [termMonths, setTermMonths] = useState(12);
  const [activationDays, setActivationDays] = useState(30);
  const [notes, setNotes] = useState('');
  const [seatQuantities, setSeatQuantities] = useState<Record<string, number>>({ UNIT_ADMIN: 1, UNIT_SUPERUSER: 0, UNIT_TECH: 0, UNIT_WAREHOUSE: 0, UNIT_DEVICE_EXPERT: 0 });
  const [result, setResult] = useState<IssuedLicenseResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    void fetchPlatformSupportLicenseTargets(token)
      .then(setTargets)
      .catch(err => setError(err instanceof Error ? err.message : 'Licence targets could not be loaded'))
      .finally(() => setTargetsLoading(false));
  }, [token]);

  const targetOptions = targetMode === 'EXISTING_GROUP' ? targets?.orgs || [] : targets?.units || [];
  const selectedTarget = targetOptions.find(target => target.id === targetId) || null;
  const seatInputs = targetMode === 'EXISTING_GROUP' ? ORG_SEAT_INPUTS : UNIT_SEAT_INPUTS;

  const changeTargetMode = (value: 'NEW_UNIT' | 'EXISTING_UNIT' | 'EXISTING_GROUP') => {
    setTargetMode(value);
    setTargetId('');
    setCustomerName('');
    setSeatQuantities(value === 'EXISTING_GROUP'
      ? { ORG_ADMIN: 1, ORG_ANALYST: 0 }
      : { UNIT_ADMIN: 1, UNIT_SUPERUSER: 0, UNIT_TECH: 0, UNIT_WAREHOUSE: 0, UNIT_DEVICE_EXPERT: 0 });
  };

  const selectExistingTarget = (id: string) => {
    setTargetId(id);
    const selected = targetOptions.find(target => target.id === id);
    if (!selected) return;
    const adminCode = targetMode === 'EXISTING_GROUP' ? 'ORG_ADMIN' : 'UNIT_ADMIN';
    setSeatQuantities({ ...selected.requiredSeats, [adminCode]: Math.max(1, selected.requiredSeats[adminCode] || 0) });
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const issued = await issuePlatformSupportLicense(token, {
        targetMode,
        customerName: targetMode === 'NEW_UNIT' ? customerName.trim() : undefined,
        targetId: targetMode === 'NEW_UNIT' ? undefined : targetId,
        termMonths,
        activationDays,
        notes: notes.trim(),
        seatQuantities
      });
      setResult(issued);
      onIssued();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Licence could not be issued');
    } finally {
      setSubmitting(false);
    }
  };

  const copyKey = async () => {
    if (!result?.activationKey) return;
    try {
      await navigator.clipboard.writeText(result.activationKey);
      setCopied(true);
    } catch {
      setError('The key could not be copied automatically. Select and copy it manually.');
    }
  };

  return <div className="platform-support-modal-backdrop" style={styles.modalBackdrop} role="presentation">
    <section style={styles.modalCard} role="dialog" aria-modal="true" aria-labelledby="issue-unit-license-title">
      <div style={styles.modalHeading}>
        <div><div style={styles.eyebrow}>SUPPORT ADMIN</div><h2 id="issue-unit-license-title" style={styles.modalTitle}>{result ? 'Licence issued' : 'Issue licence'}</h2></div>
        <button type="button" style={styles.secondaryButton} onClick={onClose}>{result ? 'Done' : 'Cancel'}</button>
      </div>
      {error && <div style={styles.error}>{error}</div>}
      {result ? <div style={styles.issuedResult}>
        <div style={styles.successBanner}><strong>{result.license.licenseNumber}</strong><span>The licence was created and audited.</span></div>
        {result.activationKey ? <>
          <div style={styles.oneTimeKeyWarning}><strong>Copy this activation key now</strong><span>It is shown only in this response and cannot be recovered later.</span></div>
          <div style={styles.activationKeyRow}>
            <code style={styles.activationKey}>{result.activationKey}</code>
            <button type="button" style={styles.copyButton} onClick={copyKey}>{copied ? 'Copied' : 'Copy key'}</button>
          </div>
        </> : <div style={styles.successBanner}><strong>Attached directly</strong><span>This licence is active for the selected existing {result.license.scopeType === 'GROUP' ? 'ORG' : 'UNIT'}; no activation key is required.</span></div>}
        <div className="platform-support-license-meta" style={styles.issuedSummary}>
          <span><b>Customer</b>{result.license.customerName}</span>
          <span><b>Licence term</b>{result.license.termMonths} months</span>
          <span><b>{result.activationKey ? 'Activation expires' : 'Latest initial seat end'}</b>{formatDate(result.activationKey ? result.license.activation?.expiresAt : result.license.expiresAt)}</span>
        </div>
        <div style={styles.detailRows}>{result.license.entitlements.map(entitlement => <div className="platform-support-license-detail-row" key={entitlement.id} style={{ ...styles.detailRow, gridTemplateColumns: 'minmax(200px, 2fr) minmax(90px, 1fr)' }}><span><strong>{entitlement.product.label}</strong><small>{entitlement.product.code}</small></span><span>{entitlement.quantity} seats</span></div>)}</div>
      </div> : <form style={styles.issueForm} onSubmit={submit}>
        <label style={styles.filterLabel}>Licence target
          <select style={styles.filterInput} value={targetMode} onChange={event => changeTargetMode(event.target.value as 'NEW_UNIT' | 'EXISTING_UNIT' | 'EXISTING_GROUP')}>
            <option value="NEW_UNIT">New standalone UNIT</option>
            <option value="EXISTING_UNIT">Existing UNIT</option>
            <option value="EXISTING_GROUP">Existing ORG</option>
          </select>
        </label>
        {targetMode === 'NEW_UNIT' ? <label style={styles.filterLabel}>New UNIT name
          <input style={styles.filterInput} value={customerName} onChange={event => setCustomerName(event.target.value)} required autoFocus />
        </label> : <label style={styles.filterLabel}>Select {targetMode === 'EXISTING_GROUP' ? 'ORG' : 'UNIT'}
          <select style={styles.filterInput} value={targetId} onChange={event => selectExistingTarget(event.target.value)} required disabled={targetsLoading}>
            <option value="">{targetsLoading ? 'Loading…' : `Choose an existing ${targetMode === 'EXISTING_GROUP' ? 'ORG' : 'UNIT'}`}</option>
            {targetOptions.map(target => <TargetOption key={target.id} target={target} />)}
          </select>
          <span style={styles.fieldHelp}>Select a stored UNIT or ORG. Names are not entered manually.</span>
        </label>}
        {selectedTarget?.currentLicense && <div style={styles.currentLicenseNotice}>
          <strong>Current licence: {selectedTarget.currentLicense.licenseNumber}</strong>
          <span>{healthLabel(selectedTarget.currentLicense.status)} · expires {formatDate(selectedTarget.currentLicense.expiresAt)}</span>
          <span>This target cannot receive a duplicate initial licence.</span>
        </div>}
        <div style={styles.formColumns}>
          <label style={styles.filterLabel}>Licence term (months)<input style={styles.filterInput} type="number" min={1} step={1} value={termMonths} onChange={event => setTermMonths(Number(event.target.value))} required /></label>
          {targetMode === 'NEW_UNIT' && <label style={styles.filterLabel}>Activation window (days)<input style={styles.filterInput} type="number" min={1} step={1} value={activationDays} onChange={event => setActivationDays(Number(event.target.value))} required /></label>}
        </div>
        <fieldset style={styles.seatFieldset}><legend style={styles.seatLegend}>Initial named seats</legend><div style={styles.seatGrid}>
          {seatInputs.map(([code, label]) => <label key={code} style={styles.seatInput}><span>{label}</span><input style={styles.quantityInput} type="number" min={code === 'UNIT_ADMIN' || code === 'ORG_ADMIN' ? 1 : 0} step={1} value={seatQuantities[code] || 0} onChange={event => setSeatQuantities(current => ({ ...current, [code]: Number(event.target.value) }))} required /></label>)}
        </div></fieldset>
        <label style={styles.filterLabel}>Internal notes (optional)<textarea style={{ ...styles.filterInput, minHeight: '4.5rem', resize: 'vertical' }} value={notes} onChange={event => setNotes(event.target.value)} /></label>
        <div style={styles.modalActions}><button type="submit" style={{ ...styles.issueButton, ...(selectedTarget?.currentLicense ? styles.disabledButton : {}) }} disabled={submitting || Boolean(selectedTarget?.currentLicense)}>{selectedTarget?.currentLicense ? 'Current licence exists' : submitting ? 'Issuing…' : 'Issue licence'}</button></div>
      </form>}
    </section>
  </div>;
};

const TargetOption: React.FC<{ target: PlatformSupportLicenseTarget }> = ({ target }) => (
  <option value={target.id}>
    {target.name} ({target.slug}){target.currentLicense ? ` — ${target.currentLicense.status} ${target.currentLicense.licenseNumber}` : ''}
  </option>
);

const SummaryMetric: React.FC<{ label: string; value: number | string; tone?: 'normal' | 'warning' }> = ({ label, value, tone = 'normal' }) => (
  <div style={{ ...styles.summaryMetric, ...(tone === 'warning' ? styles.summaryMetricWarning : {}) }}>
    <strong style={styles.summaryValue}>{value}</strong><span style={styles.summaryLabel}>{label}</span>
  </div>
);

const LicenseRow: React.FC<{
  license: PlatformSupportLicense;
  expanded: boolean;
  canManage: boolean;
  graceDays: number;
  onToggle: () => void;
  onAddCapacity: (productCode?: string) => void;
  onManagePeriods: (pool: SeatPool) => void;
  onLifecycle: (action: LicenseLifecycleAction) => void;
}> = ({ license, expanded, canManage, graceDays, onToggle, onAddCapacity, onManagePeriods, onLifecycle }) => {
  const autoRenewQuantity = license.entitlements.filter(row => row.renewalStatus === 'ACTIVE').reduce((sum, row) => sum + row.renewalQuantity, 0);
  return (
  <article style={styles.licenseRow}>
    <button type="button" style={styles.licenseRowButton} onClick={onToggle} aria-expanded={expanded}>
      <span style={styles.licenseIdentity}><strong>{license.target?.name || license.customerName || 'Unassigned licence'}</strong><small>{license.licenseNumber} · {license.scopeType === 'GROUP' ? 'ORG' : 'UNIT'}</small></span>
      <span style={styles.seatSummary}><strong>{license.totals.assigned}/{license.totals.seats}</strong><small>Seats assigned</small></span>
      <span style={styles.expirySummary}><strong>{autoRenewQuantity > 0 ? `${autoRenewQuantity.toLocaleString()} auto-renew` : formatDate(license.expiresAt)}</strong><small>{autoRenewQuantity > 0 ? 'Support-managed capacity' : 'Latest seat end'}</small></span>
      <span style={{ ...styles.healthChip, ...(license.health === 'HEALTHY' ? styles.healthGood : styles.healthAttention) }}>{healthLabel(license.health)}</span>
      <span aria-hidden="true">{expanded ? '−' : '+'}</span>
    </button>
    {expanded && <div style={styles.licenseDetails}>
      <div style={styles.siteAccessBar}>
        <span style={styles.licenseIdentity}>
          <strong>Site access</strong>
          <small>{license.status === 'SUSPENDED' ? 'Suspended · all access is blocked until reactivated' : license.status === 'REVOKED' ? 'Revoked · access is permanently blocked' : license.totals.seats > 0 ? 'Active seats determine each user’s access' : 'No current seats · customer access is unavailable'}</small>
        </span>
        {canManage && license.status !== 'REVOKED' && <span style={styles.lifecycleActions}>
          {license.status === 'ACTIVE' && <button type="button" style={styles.warningButton} onClick={() => onLifecycle('SUSPEND')}>Suspend access</button>}
          {license.status === 'SUSPENDED' && <button type="button" style={styles.issueButton} onClick={() => onLifecycle('REACTIVATE')}>Reactivate access</button>}
          <button type="button" style={styles.destructiveOutlineButton} onClick={() => onLifecycle('REVOKE')}>Revoke licence</button>
        </span>}
      </div>
      <div style={styles.purchaseHeading}>
        <div><h3 style={styles.detailTitle}>Seat capacity</h3><span style={styles.mutedText}>Capacity is pooled by user type. Each type can have several independent periods.</span></div>
        {canManage && license.status !== 'REVOKED' && <button type="button" style={styles.issueButton} onClick={() => onAddCapacity()}>Add capacity plan</button>}
      </div>
      <div className="platform-support-capacity-grid" style={styles.capacityGrid}>
        {license.seatPools.map(pool => <SeatCapacityRow key={pool.product.id} pool={pool} canManage={canManage && license.status !== 'REVOKED'} graceDays={graceDays} onAdd={() => onAddCapacity(pool.product.code)} onManage={() => onManagePeriods(pool)} />)}
      </div>
      {license.activationKeys.length > 0 && <><h3 style={styles.detailTitle}>Activation keys</h3><div style={styles.detailRows}>{license.activationKeys.map(key => <div className="platform-support-license-detail-row" key={key.id} style={styles.detailRow}>
        <span><strong>{key.keyPrefix}…</strong><small>Safe prefix only</small></span><span>{healthLabel(key.status)}</span><span>Expires {formatDate(key.expiresAt)}</span><span>{key.activatedOrganization?.name || 'Not activated'}</span>
      </div>)}</div></>}
    </div>}
  </article>
  );
};

type SeatPurchase = PlatformSupportLicense['entitlements'][number];
type SeatPool = PlatformSupportLicense['seatPools'][number];
type LicenseLifecycleAction = 'SUSPEND' | 'REACTIVATE' | 'REVOKE';

const SeatCapacityRow: React.FC<{
  pool: SeatPool;
  canManage: boolean;
  graceDays: number;
  onAdd: () => void;
  onManage: () => void;
}> = ({ pool, canManage, graceDays, onAdd, onManage }) => {
  const now = Date.now();
  const changes = pool.purchases.flatMap(row => {
    const state = seatPurchaseLifecycle(row);
    if (state === 'CURRENT' && row.renewalQuantity < row.quantity) return [new Date(row.expiresAt).getTime()];
    if (state === 'SCHEDULED') return [new Date(row.startsAt).getTime()];
    return [];
  }).filter(value => Number.isFinite(value) && value > now).sort((left, right) => left - right);
  const autoRenewing = pool.purchases.filter(row => row.renewalStatus === 'ACTIVE').reduce((sum, row) => sum + row.renewalQuantity, 0);
  const expiryAttention = pool.purchases.flatMap(row => row.expiryAttention ? [row.expiryAttention] : []).sort((left, right) => left.daysRemaining - right.daysRemaining)[0];
  return <div className="platform-support-capacity-card" style={{ ...styles.capacityCard, ...(expiryAttention?.thresholdDays === 30 ? styles.expiryUrgentSurface : expiryAttention ? styles.expiryNoticeSurface : {}) }}>
    <div style={styles.capacityCardHeading}><span style={styles.licenseIdentity}><strong>{pool.product.label}</strong><small>{pool.purchases.length} period{pool.purchases.length === 1 ? '' : 's'}</small></span><span style={styles.capacityActions}><button type="button" style={styles.secondaryButton} onClick={onManage}>Periods</button>{canManage && <button type="button" style={styles.issueButton} onClick={onAdd}>Add capacity</button>}</span></div>
    <div style={styles.capacityMetrics}>
      <span style={styles.capacityMetric}><small>Capacity</small><strong>{pool.quantity.toLocaleString()}</strong></span>
      <span style={styles.capacityMetric}><small>Assigned</small><strong>{pool.assignedCount.toLocaleString()}</strong></span>
      <span style={styles.capacityMetric}><small>Available</small><strong>{pool.availableCount.toLocaleString()}</strong></span>
    </div>
    <div style={styles.capacityNextChange}><small>Next change</small><strong>{changes[0] ? formatDate(new Date(changes[0]).toISOString()) : 'None scheduled'}</strong>{expiryAttention && <span style={expiryAttention.thresholdDays === 30 ? styles.expiryUrgentChip : styles.expiryNoticeChip}>{expiryAttention.quantity.toLocaleString()} end in {expiryAttention.daysRemaining}d</span>}{autoRenewing > 0 && <span style={styles.autoRenewChip}>{autoRenewing.toLocaleString()} auto-renew</span>}{pool.overAllocationGraceEndsAt && <span style={styles.poolWarning}>Handoff ends {formatDate(pool.overAllocationGraceEndsAt)} ({graceDays} days)</span>}</div>
  </div>;
};

const LIFECYCLE_COPY: Record<LicenseLifecycleAction, { eyebrow: string; title: string; summary: string; consequence: string; confirm: string; destructive: boolean }> = {
  SUSPEND: {
    eyebrow: 'IMMEDIATE ACCESS CHANGE',
    title: 'Suspend site access',
    summary: 'All users under this licence will be blocked immediately.',
    consequence: 'No users or customer information will be deleted. Access remains blocked until a Support Admin reactivates the licence.',
    confirm: 'Confirm suspension',
    destructive: true
  },
  REACTIVATE: {
    eyebrow: 'RESTORE SITE ACCESS',
    title: 'Reactivate site access',
    summary: 'Users with valid current seat assignments will regain access.',
    consequence: 'Expired, cancelled, revoked, or otherwise invalid seats remain blocked. Reactivation does not alter any purchase dates.',
    confirm: 'Confirm reactivation',
    destructive: false
  },
  REVOKE: {
    eyebrow: 'PERMANENT ACCESS CHANGE',
    title: 'Revoke site licence',
    summary: 'This permanently revokes the licence and blocks all access under it.',
    consequence: 'Current seat assignments will end immediately. Users and customer information will be preserved, but this licence cannot be reactivated.',
    confirm: 'Permanently revoke licence',
    destructive: true
  }
};

const LicenseLifecycleModal: React.FC<{
  token: string;
  license: PlatformSupportLicense;
  action: LicenseLifecycleAction;
  onClose: () => void;
  onSaved: () => void;
}> = ({ token, license, action, onClose, onSaved }) => {
  const copy = LIFECYCLE_COPY[action];
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await changePlatformSupportLicenseLifecycle(token, license.id, { action, notes: notes.trim() });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Licence access could not be changed');
    } finally {
      setSubmitting(false);
    }
  };

  return <div className="platform-support-modal-backdrop" style={styles.modalBackdrop} role="presentation">
    <section style={{ ...styles.confirmationModalCard, ...(copy.destructive ? styles.destructiveModalCard : {}) }} role="alertdialog" aria-modal="true" aria-labelledby="license-lifecycle-title" aria-describedby="license-lifecycle-summary">
      <div style={styles.eyebrow}>{copy.eyebrow}</div>
      <h2 id="license-lifecycle-title" style={styles.modalTitle}>{copy.title}</h2>
      <div style={styles.lifecycleTarget}>
        <strong>{license.target?.name || license.customerName || 'Licence target'}</strong>
        <span>{license.licenseNumber} · {license.scopeType === 'GROUP' ? 'ORG' : 'UNIT'}</span>
      </div>
      <p id="license-lifecycle-summary" style={styles.confirmationText}>{copy.summary}</p>
      <div style={copy.destructive ? styles.confirmationWarning : styles.infoBanner}>{copy.consequence}</div>
      {error && <div style={styles.error}>{error}</div>}
      <label style={styles.filterLabel}>Internal note (optional)<textarea style={{ ...styles.filterInput, minHeight: '4rem', resize: 'vertical' }} value={notes} onChange={event => setNotes(event.target.value)} /></label>
      <div style={styles.modalActions}>
        <button type="button" style={styles.secondaryButton} onClick={onClose} disabled={submitting}>Go back</button>
        <button type="button" style={copy.destructive ? styles.destructiveButton : styles.issueButton} onClick={() => void confirm()} disabled={submitting}>{submitting ? 'Saving…' : copy.confirm}</button>
      </div>
    </section>
  </div>;
};

function toDateInput(value: string | Date) {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
}

function oneYearFromToday() {
  const date = new Date();
  date.setUTCFullYear(date.getUTCFullYear() + 1);
  return toDateInput(date);
}

function startOfSelectedDate(value: string) {
  return value ? `${value}T00:00:00.000Z` : value;
}

function endOfSelectedDate(value: string) {
  return value ? `${value}T23:59:59.999Z` : value;
}

function endDateForMonths(startValue: string, months: number) {
  const start = new Date(`${startValue}T00:00:00.000Z`);
  if (Number.isNaN(start.getTime())) return '';
  const day = start.getUTCDate();
  const target = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(day, lastDay));
  return toDateInput(target);
}

function nextRenewalDate(entitlement: SeatPurchase) {
  if (entitlement.renewalStatus !== 'ACTIVE' || entitlement.renewalQuantity <= 0) return null;
  const next = new Date(entitlement.expiresAt);
  const interval = Math.max(1, entitlement.renewalIntervalMonths || 12);
  while (next.getTime() <= Date.now()) next.setUTCMonth(next.getUTCMonth() + interval);
  return next.toISOString();
}

type CapacityPlanLine = { id: number; productCode: string; quantity: number; startsAt: string; expiresAt: string; durationMonths: 1 | 3 | 6 | 12 | 'CUSTOM'; renewalQuantity: number; renewalIntervalMonths: number };

const CapacityPlanModal: React.FC<{
  token: string;
  license: PlatformSupportLicense;
  initialProductCode?: string;
  onClose: () => void;
  onSaved: () => void;
}> = ({ token, license, initialProductCode, onClose, onSaved }) => {
  const products = license.scopeType === 'GROUP' ? ORG_SEAT_INPUTS : UNIT_SEAT_INPUTS;
  const makeLine = (id: number, productCode = initialProductCode || products[0][0]): CapacityPlanLine => ({ id, productCode, quantity: 1, startsAt: toDateInput(new Date()), expiresAt: oneYearFromToday(), durationMonths: 12, renewalQuantity: 0, renewalIntervalMonths: 12 });
  const [lines, setLines] = useState<CapacityPlanLine[]>([makeLine(1)]);
  const [nextLineId, setNextLineId] = useState(2);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const totalSeats = lines.reduce((sum, line) => sum + (Number.isFinite(line.quantity) ? Math.max(0, line.quantity) : 0), 0);

  const updateLine = (id: number, change: Partial<CapacityPlanLine>) => setLines(current => current.map(line => line.id === id ? { ...line, ...change } : line));
  const addLine = () => {
    setLines(current => [...current, makeLine(nextLineId, current[current.length - 1]?.productCode)]);
    setNextLineId(value => value + 1);
  };
  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await createPlatformSupportSeatCapacityPlan(token, license.id, {
        purchases: lines.map(line => ({ productCode: line.productCode, quantity: line.quantity, startsAt: startOfSelectedDate(line.startsAt), expiresAt: endOfSelectedDate(line.expiresAt), renewalQuantity: line.renewalQuantity, renewalIntervalMonths: line.renewalIntervalMonths })),
        notes: notes.trim()
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Seat capacity plan could not be created');
    } finally {
      setSubmitting(false);
    }
  };

  return <div className="platform-support-modal-backdrop" style={styles.modalBackdrop} role="presentation">
    <section style={styles.capacityPlanModalCard} role="dialog" aria-modal="true" aria-labelledby="capacity-plan-title">
      <div style={styles.modalHeading}>
        <div><div style={styles.eyebrow}>{license.scopeType === 'GROUP' ? 'ORG CAPACITY' : 'UNIT CAPACITY'}</div><h2 id="capacity-plan-title" style={styles.modalTitle}>Add seat capacity</h2><p style={styles.pageText}>{license.target?.name || license.customerName}</p></div>
        <button type="button" style={styles.secondaryButton} onClick={onClose}>Close</button>
      </div>
      <div style={styles.infoBanner}>One line can add thousands of seats. Use another period when the user type or dates differ; the complete plan saves together.</div>
      {error && <div style={styles.error}>{error}</div>}
      <form style={styles.issueForm} onSubmit={submit}>
        <div style={styles.capacityPlanLines}>{lines.map((line, index) => <div className="platform-support-capacity-plan-line" style={styles.capacityPlanLine} key={line.id}>
          <div style={styles.capacityPlanLineHeading}><strong>Period {index + 1}</strong>{lines.length > 1 && <button type="button" style={styles.removeLineButton} onClick={() => setLines(current => current.filter(item => item.id !== line.id))}>Remove</button>}</div>
          <div className="platform-support-capacity-plan-fields" style={styles.capacityPlanFields}>
            <label style={{ ...styles.mobileFieldLabel, ...styles.capacityPlanProduct }}><span>User type</span><select aria-label={`User type, line ${index + 1}`} style={styles.filterInput} value={line.productCode} onChange={event => updateLine(line.id, { productCode: event.target.value })}>{products.map(([code, label]) => <option key={code} value={code}>{label}</option>)}</select></label>
            <label style={styles.mobileFieldLabel}><span>Quantity</span><input aria-label={`Quantity, line ${index + 1}`} style={styles.filterInput} type="number" min={1} step={1} value={line.quantity} onChange={event => { const quantity = Number(event.target.value); updateLine(line.id, { quantity, renewalQuantity: Math.min(line.renewalQuantity, quantity) }); }} required /></label>
            <label style={styles.mobileFieldLabel}><span>Duration</span><select style={styles.filterInput} value={line.durationMonths} onChange={event => { const value = event.target.value === 'CUSTOM' ? 'CUSTOM' : Number(event.target.value) as 1 | 3 | 6 | 12; updateLine(line.id, { durationMonths: value, ...(value === 'CUSTOM' ? {} : { expiresAt: endDateForMonths(line.startsAt, value) }) }); }}><option value={1}>1 month</option><option value={3}>3 months</option><option value={6}>6 months</option><option value={12}>12 months</option><option value="CUSTOM">Custom dates</option></select></label>
            <label style={styles.mobileFieldLabel}><span>Starts</span><input aria-label={`Starts, line ${index + 1}`} style={styles.filterInput} type="date" value={line.startsAt} onChange={event => { const startsAt = event.target.value; updateLine(line.id, { startsAt, ...(line.durationMonths === 'CUSTOM' ? {} : { expiresAt: endDateForMonths(startsAt, line.durationMonths) }) }); }} required /></label>
            <label style={styles.mobileFieldLabel}><span>Ends</span><input aria-label={`Ends, line ${index + 1}`} style={styles.filterInput} type="date" min={line.startsAt} value={line.expiresAt} onChange={event => updateLine(line.id, { expiresAt: event.target.value, durationMonths: 'CUSTOM' })} required /></label>
          </div>
          <div className="platform-support-renewal-controls" style={styles.renewalControls}>
            <label style={styles.mobileFieldLabel}><span>Seats to auto-renew</span><input style={styles.filterInput} type="number" min={0} max={line.quantity} step={1} value={line.renewalQuantity} onChange={event => updateLine(line.id, { renewalQuantity: Number(event.target.value) })} /></label>
            <span style={styles.renewalQuickActions}><button type="button" style={styles.secondaryButton} onClick={() => updateLine(line.id, { renewalQuantity: line.quantity })}>Renew all</button><button type="button" style={styles.secondaryButton} onClick={() => updateLine(line.id, { renewalQuantity: 0 })}>Clear</button></span>
            <label style={styles.mobileFieldLabel}><span>Renewal interval</span><select style={styles.filterInput} value={line.renewalIntervalMonths} disabled={line.renewalQuantity === 0} onChange={event => updateLine(line.id, { renewalIntervalMonths: Number(event.target.value) })}><option value={1}>Monthly</option><option value={3}>Every 3 months</option><option value={6}>Every 6 months</option><option value={12}>Yearly</option><option value={24}>Every 2 years</option></select></label>
            <small style={styles.fieldHelp}>{line.renewalQuantity > 0 ? `${line.renewalQuantity.toLocaleString()} of ${line.quantity.toLocaleString()} seats will remain active under Support-managed renewal.` : 'These seats end on the selected date unless renewal is enabled later.'}</small>
          </div>
          <div style={styles.capacityLinePreview}><strong>{line.quantity.toLocaleString()} {SEAT_LABELS[line.productCode] || 'seat'} seat{line.quantity === 1 ? '' : 's'}</strong><span>{formatDate(startOfSelectedDate(line.startsAt))} – {formatDate(endOfSelectedDate(line.expiresAt))}</span></div>
        </div>)}</div>
        <button type="button" style={styles.addLineButton} onClick={addLine}>+ Add another period</button>
        <label style={styles.filterLabel}>Internal note (optional)<textarea style={{ ...styles.filterInput, minHeight: '4rem', resize: 'vertical' }} value={notes} onChange={event => setNotes(event.target.value)} /></label>
        <div style={styles.modalActions}><button type="submit" style={styles.issueButton} disabled={submitting}>{submitting ? 'Saving plan…' : `Add ${totalSeats.toLocaleString()} seat${totalSeats === 1 ? '' : 's'} across ${lines.length} period${lines.length === 1 ? '' : 's'}`}</button></div>
      </form>
    </section>
  </div>;
};

const SeatPeriodsModal: React.FC<{
  license: PlatformSupportLicense;
  pool: SeatPool;
  canManage: boolean;
  onClose: () => void;
  onAdd: () => void;
  onManage: (entitlement: SeatPurchase) => void;
}> = ({ license, pool, canManage, onClose, onAdd, onManage }) => {
  const purchases = [...pool.purchases].sort((left, right) => new Date(right.startsAt).getTime() - new Date(left.startsAt).getTime());
  return <div className="platform-support-modal-backdrop" style={styles.modalBackdrop} role="presentation">
    <section style={styles.modalCard} role="dialog" aria-modal="true" aria-labelledby="seat-periods-title">
      <div style={styles.modalHeading}>
        <div><div style={styles.eyebrow}>SEAT PERIODS</div><h2 id="seat-periods-title" style={styles.modalTitle}>{pool.product.label}</h2><p style={styles.pageText}>{license.target?.name || license.customerName} · {pool.quantity.toLocaleString()} current capacity</p></div>
        <button type="button" style={styles.secondaryButton} onClick={onClose}>Close</button>
      </div>
      <div className="platform-support-data-grid" style={styles.periodSummary}><span style={styles.dataMetric}><b>Current capacity</b><strong>{pool.quantity.toLocaleString()}</strong></span><span style={styles.dataMetric}><b>Assigned</b><strong>{pool.assignedCount.toLocaleString()}</strong></span><span style={styles.dataMetric}><b>Available</b><strong>{pool.availableCount.toLocaleString()}</strong></span></div>
      <div style={styles.periodRows}>{purchases.map(entitlement => <div className="platform-support-period-row" style={{ ...styles.periodRow, ...(entitlement.expiryAttention?.thresholdDays === 30 ? styles.expiryUrgentSurface : entitlement.expiryAttention ? styles.expiryNoticeSurface : {}) }} key={entitlement.id}>
        <span style={styles.purchaseMetric}><strong>{entitlement.quantity.toLocaleString()} seats</strong><small>{entitlement.renewalQuantity > 0 ? `${entitlement.renewalQuantity.toLocaleString()} auto-renew` : entitlement.cost.label}</small></span>
        <span style={styles.purchasePeriod}><strong>{formatDate(entitlement.startsAt)} – {formatDate(entitlement.expiresAt)}</strong><small>{nextRenewalDate(entitlement) ? `Next renewal ${formatDate(nextRenewalDate(entitlement))}` : entitlement.termType === 'PRIMARY_ADMIN' ? 'Initial account capacity' : 'Independent period'}</small></span>
        <span style={{ ...styles.readOnlyChip, ...(entitlement.expiryAttention?.thresholdDays === 30 ? styles.expiryUrgentChip : entitlement.expiryAttention ? styles.expiryNoticeChip : entitlement.renewalQuantity > 0 ? styles.autoRenewChip : {}), justifySelf: 'start' }}>{entitlement.expiryAttention ? `${entitlement.expiryAttention.quantity} end in ${entitlement.expiryAttention.daysRemaining}d` : entitlement.renewalQuantity > 0 ? 'Auto-renew' : healthLabel(seatPurchaseLifecycle(entitlement))}</span>
        {canManage && entitlement.status === 'ACTIVE' ? <button type="button" style={styles.secondaryButton} onClick={() => onManage(entitlement)}>Edit</button> : <span />}
      </div>)}</div>
      {canManage && license.status !== 'REVOKED' && <div style={styles.modalActions}><button type="button" style={styles.issueButton} onClick={onAdd}>Add {pool.product.label} capacity</button></div>}
    </section>
  </div>;
};

const ManageSeatPurchaseModal: React.FC<{
  token: string;
  license: PlatformSupportLicense;
  entitlement: SeatPurchase;
  graceDays: number;
  onClose: () => void;
  onSaved: () => void;
}> = ({ token, license, entitlement, graceDays, onClose, onSaved }) => {
  const primaryAdmin = entitlement.termType === 'PRIMARY_ADMIN';
  const seatPool = license.seatPools.find(pool => pool.product.id === entitlement.product.id);
  const isCurrentPurchase = seatPurchaseLifecycle(entitlement) === 'CURRENT';
  const [quantity, setQuantity] = useState(entitlement.quantity);
  const [expiresAt, setExpiresAt] = useState(toDateInput(entitlement.expiresAt));
  const [renewalQuantity, setRenewalQuantity] = useState(entitlement.renewalQuantity || 0);
  const [renewalIntervalMonths, setRenewalIntervalMonths] = useState(entitlement.renewalIntervalMonths || 12);
  const [notes, setNotes] = useState(entitlement.notes || '');
  const [submitting, setSubmitting] = useState(false);
  const [confirmingReduction, setConfirmingReduction] = useState(false);
  const [confirmingCancellation, setConfirmingCancellation] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const originalContribution = isCurrentPurchase ? (new Date(entitlement.expiresAt) > new Date() ? entitlement.quantity : entitlement.renewalQuantity) : 0;
  const projectedContribution = isCurrentPurchase ? (new Date(endOfSelectedDate(expiresAt)) > new Date() ? quantity : renewalQuantity) : 0;
  const projectedPoolQuantity = Math.max(0, Number(seatPool?.quantity || 0) - originalContribution + projectedContribution);
  const reduction = isCurrentPurchase && projectedPoolQuantity < Number(seatPool?.assignedCount || 0);

  const save = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await changePlatformSupportSeatPurchase(token, license.id, entitlement.id, { quantity, expiresAt: endOfSelectedDate(expiresAt), renewalQuantity, renewalIntervalMonths, notes: notes.trim() });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Seat purchase could not be changed');
    } finally {
      setSubmitting(false);
    }
  };

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (reduction) {
      setConfirmingReduction(true);
      return;
    }
    void save();
  };

  const excessAssignments = Math.max(0, Number(seatPool?.assignedCount || 0) - projectedPoolQuantity);
  const cancellationPoolQuantity = Math.max(0, Number(seatPool?.quantity || 0) - originalContribution);
  const cancellationExcess = Math.max(0, Number(seatPool?.assignedCount || 0) - cancellationPoolQuantity);
  const customerAdminLabel = license.scopeType === 'GROUP' ? 'ORG Admin' : 'UNIT Admin';

  const cancelPurchase = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await cancelPlatformSupportSeatPurchase(token, license.id, entitlement.id, { notes: notes.trim() });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Seat purchase could not be cancelled');
    } finally {
      setSubmitting(false);
    }
  };

  return <>
  <div className="platform-support-modal-backdrop" style={styles.modalBackdrop} role="presentation">
    <section style={styles.compactModalCard} role="dialog" aria-modal="true" aria-labelledby="manage-seat-purchase-title">
      <div style={styles.modalHeading}>
        <div><div style={styles.eyebrow}>{primaryAdmin ? 'PRIMARY ADMIN' : 'USER LICENCE PURCHASE'}</div><h2 id="manage-seat-purchase-title" style={styles.modalTitle}>{entitlement.product.label}</h2><p style={styles.pageText}>{license.target?.name || license.customerName}</p></div>
        <button type="button" style={styles.secondaryButton} onClick={onClose}>Close</button>
      </div>
      {error && <div style={styles.error}>{error}</div>}
      <form style={styles.issueForm} onSubmit={submit}>
        <div className="platform-support-data-grid" style={styles.currentPurchaseSummary}><span style={styles.dataMetric}><b>Purchase quantity</b><strong>{entitlement.quantity.toLocaleString()}</strong></span><span style={styles.dataMetric}><b>Current pool</b><strong>{seatPool?.assignedCount || 0} assigned / {seatPool?.quantity || 0}</strong></span><span style={{ ...styles.dataMetric, ...styles.purchasePeriodSummary }}><b>Purchase period</b><strong>{formatDate(entitlement.startsAt)} – {formatDate(entitlement.expiresAt)}</strong></span></div>
        <div style={styles.formColumns}>
          <label style={styles.filterLabel}>Purchased seats<input style={styles.filterInput} type="number" min={0} step={1} value={quantity} onChange={event => { const next = Number(event.target.value); setQuantity(next); setRenewalQuantity(current => Math.min(current, next)); }} required /></label>
          <label style={styles.filterLabel}>Access ends<input style={styles.filterInput} type="date" min={toDateInput(entitlement.startsAt)} value={expiresAt} onChange={event => setExpiresAt(event.target.value)} required /></label>
        </div>
        <fieldset style={styles.seatFieldset}><legend style={styles.seatLegend}>Auto-renewal</legend><div style={styles.renewalEditGrid}>
          <label style={styles.filterLabel}>Seats to auto-renew<input style={styles.filterInput} type="number" min={0} max={quantity} step={1} value={renewalQuantity} onChange={event => setRenewalQuantity(Number(event.target.value))} /></label>
          <label style={styles.filterLabel}>Renewal interval<select style={styles.filterInput} value={renewalIntervalMonths} disabled={renewalQuantity === 0} onChange={event => setRenewalIntervalMonths(Number(event.target.value))}><option value={1}>Monthly</option><option value={3}>Every 3 months</option><option value={6}>Every 6 months</option><option value={12}>Yearly</option><option value={24}>Every 2 years</option></select></label>
          <span style={styles.renewalQuickActions}><button type="button" style={styles.secondaryButton} onClick={() => setRenewalQuantity(quantity)}>Renew all</button><button type="button" style={styles.secondaryButton} onClick={() => setRenewalQuantity(0)}>Clear</button></span>
          <small style={styles.fieldHelp}>{renewalQuantity > 0 ? `${renewalQuantity.toLocaleString()} of ${quantity.toLocaleString()} seats will remain active after this period ends.` : 'All seats will end on the access-end date.'}</small>
        </div></fieldset>
        {primaryAdmin && <div style={styles.infoBanner}>This was the initial Admin purchase used to establish the account. It now follows the same independent seat rules as every other purchase.</div>}
        {reduction && <div style={styles.warningBanner}><strong>This creates a pooled-seat over-allocation</strong><span>{excessAssignments} assigned user{excessAssignments === 1 ? '' : 's'} will enter the {graceDays}-day remediation period. Spare capacity from overlapping purchases has already been counted.</span></div>}
        <label style={styles.filterLabel}>Internal note (optional)<textarea style={{ ...styles.filterInput, minHeight: '4rem', resize: 'vertical' }} value={notes} onChange={event => setNotes(event.target.value)} /></label>
        <div style={styles.purchaseModalActions}>
          <span>{entitlement.status === 'ACTIVE' && <button type="button" style={styles.destructiveOutlineButton} onClick={() => setConfirmingCancellation(true)}>Cancel purchase</button>}</span>
          <span style={styles.modalActions}><button type="submit" style={styles.issueButton} disabled={submitting}>{submitting ? 'Saving…' : 'Save changes'}</button></span>
        </div>
      </form>
    </section>
  </div>
  {confirmingReduction && <div className="platform-support-modal-backdrop" style={{ ...styles.modalBackdrop, zIndex: 1010 }} role="presentation">
    <section style={{ ...styles.confirmationModalCard, ...styles.destructiveModalCard }} role="alertdialog" aria-modal="true" aria-labelledby="confirm-seat-reduction-title" aria-describedby="confirm-seat-reduction-description">
      <div style={styles.eyebrow}>ACCESS IMPACT WARNING</div>
      <h2 id="confirm-seat-reduction-title" style={styles.modalTitle}>Confirm seat reduction</h2>
      <p id="confirm-seat-reduction-description" style={styles.confirmationText}>The resulting combined seat pool is smaller than the number of assigned users. Confirming starts the {graceDays}-day remediation period.</p>
      <div className="platform-support-reduction-impact" style={styles.reductionImpactGrid}>
        <span style={styles.reductionImpactMetric}><b>Currently assigned</b><strong>{seatPool?.assignedCount || 0}</strong></span>
        <span style={styles.reductionImpactMetric}><b>New pool capacity</b><strong>{projectedPoolQuantity}</strong></span>
        <span style={styles.reductionImpactMetric}><b>Seats to release</b><strong>{excessAssignments}</strong></span>
      </div>
      <div style={styles.confirmationWarning}>
        <strong>What happens next</strong>
        <span>No user is removed immediately.</span>
        <span>The {customerAdminLabel} has {graceDays} days to release {excessAssignments} assignment{excessAssignments === 1 ? '' : 's'}. If unresolved, the newest excess assignment{excessAssignments === 1 ? '' : 's'} will be suspended automatically.</span>
      </div>
      <div style={styles.modalActions}>
        <button type="button" style={styles.secondaryButton} onClick={() => setConfirmingReduction(false)} disabled={submitting}>Go back</button>
        <button type="button" style={styles.destructiveButton} onClick={() => void save()} disabled={submitting}>{submitting ? 'Reducing seats…' : 'Confirm seat reduction'}</button>
      </div>
    </section>
  </div>}
  {confirmingCancellation && <div className="platform-support-modal-backdrop" style={{ ...styles.modalBackdrop, zIndex: 1010 }} role="presentation">
    <section style={{ ...styles.confirmationModalCard, ...styles.destructiveModalCard }} role="alertdialog" aria-modal="true" aria-labelledby="confirm-purchase-cancellation-title" aria-describedby="confirm-purchase-cancellation-description">
      <div style={styles.eyebrow}>SEAT CAPACITY CHANGE</div>
      <h2 id="confirm-purchase-cancellation-title" style={styles.modalTitle}>Cancel this purchase?</h2>
      <div style={styles.lifecycleTarget}><strong>{entitlement.product.label}</strong><span>{entitlement.quantity} purchased · pool has {seatPool?.assignedCount || 0} assigned · ends {formatDate(entitlement.expiresAt)}</span></div>
      <p id="confirm-purchase-cancellation-description" style={styles.confirmationText}>Cancellation is immediate and final for this purchase batch.</p>
      <div style={styles.confirmationWarning}>
        <strong>{cancellationExcess > 0 ? `${cancellationExcess} excess assignment${cancellationExcess === 1 ? '' : 's'} will enter remediation.` : 'The remaining pooled capacity can cover every assignment.'}</strong>
        <span>If capacity is insufficient, the {graceDays}-day handoff applies. No user or customer information is deleted, and spare capacity in overlapping purchases remains available.</span>
      </div>
      {error && <div style={styles.error}>{error}</div>}
      <div style={styles.modalActions}>
        <button type="button" style={styles.secondaryButton} onClick={() => setConfirmingCancellation(false)} disabled={submitting}>Go back</button>
        <button type="button" style={styles.destructiveButton} onClick={() => void cancelPurchase()} disabled={submitting}>{submitting ? 'Cancelling…' : 'Cancel purchase now'}</button>
      </div>
    </section>
  </div>}
  </>;
};

const styles: Record<string, React.CSSProperties> = {
  loading: { minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#F3F0EA', color: '#124B50' },
  loginPage: { minHeight: '100vh', display: 'grid', placeItems: 'center', padding: '1.25rem', background: 'linear-gradient(145deg, #0C353B 0%, #123F46 42%, #EDE8DF 42%)' },
  loginCard: { width: 'min(420px, 100%)', display: 'grid', gap: '0.65rem', padding: '1.55rem', border: '1px solid #D9CDBD', borderRadius: '1rem', background: '#FFFDF9', boxShadow: '0 24px 60px rgba(5, 39, 43, 0.24)' },
  brandMark: { color: '#0C4A50', fontSize: '1.35rem', fontWeight: 800, letterSpacing: '-0.04em' },
  eyebrow: { color: '#A36B22', fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.1em' },
  loginTitle: { margin: 0, color: '#073F46', fontSize: '1.65rem' },
  loginText: { margin: '0 0 0.35rem', color: '#627477', fontSize: '0.9rem', lineHeight: 1.5 },
  label: { color: '#173F43', fontSize: '0.8rem', fontWeight: 750 },
  input: { minWidth: 0, border: '1px solid #CEC5B7', borderRadius: '0.55rem', padding: '0.68rem 0.72rem', background: '#FFF', color: '#173F43' },
  passwordRow: { display: 'flex', gap: '0.5rem' },
  primaryButton: { marginTop: '0.35rem', border: '1px solid #0E5055', borderRadius: '0.55rem', padding: '0.7rem 0.9rem', background: '#0E5055', color: '#FFF', fontWeight: 750, cursor: 'pointer' },
  secondaryButton: { border: '1px solid #CEC5B7', borderRadius: '0.55rem', padding: '0.58rem 0.72rem', background: '#FFFDF9', color: '#173F43', fontWeight: 700, cursor: 'pointer' },
  warningButton: { border: '1px solid #C98524', borderRadius: '0.55rem', padding: '0.58rem 0.72rem', background: '#FFF4D8', color: '#744A16', fontWeight: 750, cursor: 'pointer' },
  destructiveOutlineButton: { border: '1px solid #C96A62', borderRadius: '0.55rem', padding: '0.58rem 0.72rem', background: '#FFFDF9', color: '#A9362F', fontWeight: 750, cursor: 'pointer' },
  productLink: { justifySelf: 'center', marginTop: '0.25rem', color: '#48676A', fontSize: '0.78rem' },
  error: { padding: '0.65rem 0.75rem', border: '1px solid #E1B5AE', borderRadius: '0.55rem', background: '#FBE8E5', color: '#96372F', fontSize: '0.82rem' },
  consoleShell: { minHeight: '100vh', display: 'grid', gridTemplateRows: 'auto 1fr', background: '#F3F0EA', color: '#173F43' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', padding: '0.75rem 1rem', background: '#0C353B', color: '#FFF' },
  headerBrand: { fontSize: '1.12rem', fontWeight: 800 },
  headerProduct: { color: '#BFD4D1', fontSize: '0.72rem' },
  headerAccount: { display: 'flex', alignItems: 'center', gap: '0.8rem', textAlign: 'right', fontSize: '0.8rem' },
  accountRole: { display: 'block', color: '#BFD4D1', fontSize: '0.68rem' },
  headerButton: { border: '1px solid #5B7C7E', borderRadius: '0.45rem', padding: '0.42rem 0.65rem', background: '#174C51', color: '#FFF', cursor: 'pointer' },
  workspace: { minHeight: 0 },
  navigation: { gap: '0.35rem', padding: '1rem 0.75rem', background: '#E8E3DA', borderRight: '1px solid #D7CCBD' },
  navItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', padding: '0.62rem 0.7rem', borderRadius: '0.55rem', color: '#4E6568', fontSize: '0.82rem', fontWeight: 700 },
  navButton: { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', padding: '0.62rem 0.7rem', border: 0, borderRadius: '0.55rem', background: 'transparent', color: '#4E6568', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', textAlign: 'left' },
  navItemActive: { background: '#FFFDF9', color: '#073F46', boxShadow: '0 2px 8px rgba(13, 65, 70, 0.08)' },
  nextChip: { padding: '0.12rem 0.38rem', borderRadius: '999px', background: '#D9ECE5', color: '#24634E', fontSize: '0.58rem', textTransform: 'uppercase' },
  liveChip: { padding: '0.12rem 0.38rem', borderRadius: '999px', background: '#D9ECE5', color: '#24634E', fontSize: '0.58rem', textTransform: 'uppercase' },
  plannedChip: { padding: '0.12rem 0.38rem', borderRadius: '999px', background: '#E0DDD7', color: '#6C7677', fontSize: '0.58rem', textTransform: 'uppercase' },
  content: { minWidth: 0, display: 'grid', alignContent: 'start', gap: '1rem', padding: '1.2rem' },
  pageHeading: { display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '1rem', flexWrap: 'wrap' },
  pageTitle: { margin: '0.2rem 0 0', color: '#073F46', fontSize: '1.55rem' },
  pageText: { margin: '0.25rem 0 0', color: '#627477', fontSize: '0.88rem' },
  realmChip: { padding: '0.35rem 0.6rem', borderRadius: '999px', background: '#D9ECE5', color: '#24634E', fontSize: '0.7rem', fontWeight: 800 },
  boundaryCard: { display: 'flex', alignItems: 'center', gap: '0.8rem', padding: '0.9rem 1rem', border: '1px solid #BED8CD', borderRadius: '0.8rem', background: '#EAF5EF' },
  boundaryIcon: { width: '2rem', height: '2rem', display: 'grid', placeItems: 'center', flex: '0 0 auto', borderRadius: '50%', background: '#2D755B', color: '#FFF', fontWeight: 900 },
  cardTitle: { margin: 0, color: '#073F46', fontSize: '0.98rem' },
  cardText: { margin: '0.25rem 0 0', color: '#627477', fontSize: '0.82rem', lineHeight: 1.5 },
  moduleGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.8rem' },
  moduleCard: { padding: '1rem', border: '1px solid #D9CDBD', borderRadius: '0.8rem', background: '#FFFDF9' },
  moduleLabel: { marginBottom: '0.35rem', color: '#A36B22', fontSize: '0.62rem', fontWeight: 850, letterSpacing: '0.08em' },
  readOnlyChip: { padding: '0.35rem 0.6rem', borderRadius: '999px', background: '#E9E4DC', color: '#5E6C6D', fontSize: '0.7rem', fontWeight: 800 },
  issueButton: { border: '1px solid #0E5055', borderRadius: '0.55rem', padding: '0.62rem 0.85rem', background: '#0E5055', color: '#FFF', fontWeight: 750, cursor: 'pointer' },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.7rem' },
  summaryMetric: { display: 'grid', gap: '0.1rem', padding: '0.8rem 0.9rem', border: '1px solid #BED8CD', borderRadius: '0.7rem', background: '#EAF5EF' },
  summaryMetricWarning: { borderColor: '#E5C88B', background: '#FFF4D8' },
  summaryValue: { color: '#073F46', fontSize: '1.25rem' },
  summaryLabel: { color: '#627477', fontSize: '0.72rem' },
  filterCard: { display: 'grid', gridTemplateColumns: 'minmax(220px, 2fr) minmax(150px, 1fr) minmax(150px, 1fr)', gap: '0.7rem', padding: '0.8rem', border: '1px solid #D9CDBD', borderRadius: '0.8rem', background: '#FFFDF9' },
  filterLabel: { display: 'grid', gap: '0.28rem', color: '#173F43', fontSize: '0.72rem', fontWeight: 750 },
  filterInput: { minWidth: 0, border: '1px solid #CEC5B7', borderRadius: '0.5rem', padding: '0.6rem 0.65rem', background: '#FFF', color: '#173F43' },
  fieldHelp: { color: '#6D7E80', fontSize: '0.67rem', fontWeight: 500, lineHeight: 1.4 },
  currentLicenseNotice: { display: 'grid', gap: '0.18rem', padding: '0.7rem', border: '1px solid #E5C88B', borderRadius: '0.55rem', background: '#FFF4D8', color: '#744A16', fontSize: '0.75rem' },
  disabledButton: { background: '#8AA09F', borderColor: '#8AA09F', cursor: 'not-allowed' },
  licenseListCard: { minWidth: 0, padding: '0.85rem', border: '1px solid #D9CDBD', borderRadius: '0.8rem', background: '#FFFDF9' },
  listHeading: { display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center', marginBottom: '0.7rem' },
  mutedText: { display: 'block', marginTop: '0.15rem', color: '#6D7E80', fontSize: '0.72rem' },
  loadingChip: { padding: '0.3rem 0.55rem', borderRadius: '999px', background: '#E6F1EE', color: '#24634E', fontSize: '0.7rem', fontWeight: 750 },
  emptyState: { padding: '2rem 1rem', textAlign: 'center', color: '#6D7E80' },
  licenseList: { display: 'grid', gap: '0.55rem' },
  licenseRow: { overflow: 'hidden', border: '1px solid #D9CDBD', borderRadius: '0.65rem', background: '#FFF' },
  licenseRowButton: { width: '100%', display: 'grid', gridTemplateColumns: 'minmax(210px, 2fr) minmax(90px, 0.7fr) minmax(125px, 0.9fr) auto auto', gap: '0.8rem', alignItems: 'center', padding: '0.75rem', border: 0, background: '#FFF', color: '#173F43', textAlign: 'left', cursor: 'pointer' },
  licenseIdentity: { display: 'grid', gap: '0.12rem' },
  seatSummary: { display: 'grid', gap: '0.12rem' },
  expirySummary: { display: 'grid', gap: '0.12rem' },
  healthChip: { justifySelf: 'start', padding: '0.25rem 0.48rem', borderRadius: '999px', fontSize: '0.62rem', fontWeight: 800, whiteSpace: 'nowrap' },
  healthGood: { background: '#DFF0E8', color: '#24634E' },
  healthAttention: { background: '#F9E7C2', color: '#8A5819' },
  licenseDetails: { display: 'grid', gap: '0.7rem', padding: '0.85rem', borderTop: '1px solid #D9CDBD', background: '#F7F4EE' },
  siteAccessBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap', padding: '0.75rem', border: '1px solid #D7E3DE', borderRadius: '0.6rem', background: '#EEF5F2' },
  lifecycleActions: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', flexWrap: 'wrap' },
  purchaseHeading: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap' },
  poolWarning: { color: '#744A16', fontSize: '0.7rem' },
  capacityGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.65rem' },
  capacityCard: { display: 'grid', gap: '0.7rem', minWidth: 0, padding: '0.8rem', border: '1px solid #BED8CD', borderRadius: '0.65rem', background: '#FFF' },
  capacityCardHeading: { display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '0.75rem' },
  capacityMetrics: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', overflow: 'hidden', border: '1px solid #D7E3DE', borderRadius: '0.55rem', background: '#F4F8F6' },
  capacityMetric: { display: 'grid', gap: '0.18rem', padding: '0.6rem 0.7rem', borderRight: '1px solid #D7E3DE' },
  capacityNextChange: { display: 'flex', alignItems: 'baseline', gap: '0.45rem', flexWrap: 'wrap', color: '#173F43' },
  autoRenewChip: { padding: '0.22rem 0.45rem', borderRadius: '999px', background: '#DFF0E8', color: '#24634E', fontSize: '0.66rem', fontWeight: 800 },
  expiryNoticeChip: { padding: '0.22rem 0.45rem', borderRadius: '999px', background: '#FFF4D8', color: '#744A16', fontSize: '0.66rem', fontWeight: 800 },
  expiryUrgentChip: { padding: '0.22rem 0.45rem', borderRadius: '999px', background: '#FBE8E5', color: '#96372F', fontSize: '0.66rem', fontWeight: 800 },
  expiryNoticeSurface: { borderColor: '#E5C88B', background: '#FFFBF0' },
  expiryUrgentSurface: { borderColor: '#DFA39B', background: '#FFF4F2' },
  capacityActions: { display: 'flex', justifyContent: 'flex-end', gap: '0.4rem' },
  purchaseMetric: { display: 'grid', gap: '0.08rem' },
  purchasePeriod: { display: 'grid', gap: '0.08rem' },
  detailMeta: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.5rem' },
  detailTitle: { margin: '0.15rem 0 0', color: '#073F46', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' },
  detailRows: { display: 'grid', gap: '0.35rem' },
  detailRow: { display: 'grid', gridTemplateColumns: 'minmax(200px, 2fr) repeat(3, minmax(90px, 1fr))', gap: '0.6rem', alignItems: 'center', padding: '0.55rem 0.65rem', borderRadius: '0.5rem', background: '#FFF', fontSize: '0.75rem' },
  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.7rem', marginTop: '0.8rem', color: '#627477', fontSize: '0.75rem' },
  modalBackdrop: { position: 'fixed', inset: 0, zIndex: 1000, display: 'grid', placeItems: 'center', padding: '1rem', background: 'rgba(6, 35, 39, 0.58)' },
  modalCard: { width: 'min(720px, 100%)', maxHeight: 'calc(100vh - 2rem)', overflowY: 'auto', display: 'grid', gap: '1rem', padding: '1.2rem', border: '1px solid #D9CDBD', borderRadius: '0.9rem', background: '#FFFDF9', boxShadow: '0 22px 70px rgba(4, 31, 34, 0.32)' },
  compactModalCard: { width: 'min(640px, 100%)', maxHeight: 'calc(100vh - 2rem)', overflowY: 'auto', display: 'grid', gap: '1rem', padding: '1.2rem', border: '1px solid #D9CDBD', borderRadius: '0.9rem', background: '#FFFDF9', boxShadow: '0 22px 70px rgba(4, 31, 34, 0.32)' },
  capacityPlanModalCard: { width: 'min(820px, 100%)', maxHeight: 'calc(100vh - 2rem)', overflowY: 'auto', display: 'grid', gap: '1rem', padding: '1.2rem', border: '1px solid #D9CDBD', borderRadius: '0.9rem', background: '#FFFDF9', boxShadow: '0 22px 70px rgba(4, 31, 34, 0.32)' },
  modalHeading: { display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '1rem' },
  modalTitle: { margin: '0.2rem 0 0', color: '#073F46', fontSize: '1.25rem' },
  issueForm: { display: 'grid', gap: '0.8rem' },
  formColumns: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.7rem' },
  seatFieldset: { margin: 0, padding: '0.75rem', border: '1px solid #D9CDBD', borderRadius: '0.65rem' },
  seatLegend: { padding: '0 0.3rem', color: '#173F43', fontSize: '0.76rem', fontWeight: 800 },
  seatGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))', gap: '0.5rem' },
  seatInput: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', padding: '0.5rem', borderRadius: '0.5rem', background: '#F5F1EA', color: '#173F43', fontSize: '0.72rem', fontWeight: 700 },
  quantityInput: { width: '4rem', border: '1px solid #CEC5B7', borderRadius: '0.4rem', padding: '0.4rem', background: '#FFF', color: '#173F43' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '0.55rem' },
  purchaseModalActions: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' },
  capacityPlanLines: { display: 'grid', gap: '0.5rem' },
  capacityPlanLine: { display: 'grid', gap: '0.6rem', padding: '0.75rem', border: '1px solid #D7E3DE', borderRadius: '0.6rem', background: '#F7FAF8' },
  capacityPlanLineHeading: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '1.5rem', color: '#073F46', fontSize: '0.78rem' },
  capacityPlanFields: { display: 'grid', gridTemplateColumns: 'minmax(0, 1.35fr) minmax(90px, 0.5fr) minmax(110px, 0.65fr) minmax(135px, 0.78fr) minmax(135px, 0.78fr)', gap: '0.6rem' },
  capacityPlanProduct: { minWidth: 0 },
  renewalControls: { display: 'grid', gridTemplateColumns: 'minmax(130px, 0.65fr) auto minmax(170px, 0.9fr) minmax(220px, 1.2fr)', gap: '0.6rem', alignItems: 'end', paddingTop: '0.6rem', borderTop: '1px solid #D7E3DE' },
  renewalQuickActions: { display: 'flex', gap: '0.4rem', alignItems: 'end' },
  renewalEditGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.6rem', alignItems: 'end' },
  capacityLinePreview: { display: 'flex', justifyContent: 'space-between', gap: '0.6rem', flexWrap: 'wrap', padding: '0.55rem 0.65rem', borderRadius: '0.5rem', background: '#EAF5EF', color: '#24634E', fontSize: '0.72rem' },
  mobileFieldLabel: { display: 'grid', gap: '0.2rem', color: '#173F43', fontSize: '0.68rem', fontWeight: 750 },
  removeLineButton: { border: '1px solid #D9CDBD', borderRadius: '0.5rem', padding: '0.6rem', background: '#FFF', color: '#8B4B45', fontWeight: 700, cursor: 'pointer' },
  addLineButton: { justifySelf: 'start', border: 0, padding: '0.35rem 0', background: 'transparent', color: '#0E5055', fontWeight: 800, cursor: 'pointer' },
  periodSummary: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.5rem' },
  periodRows: { display: 'grid', overflow: 'hidden', border: '1px solid #D7E3DE', borderRadius: '0.55rem' },
  periodRow: { display: 'grid', gridTemplateColumns: 'minmax(100px, 0.6fr) minmax(260px, 1.6fr) minmax(90px, 0.5fr) auto', gap: '0.7rem', alignItems: 'center', padding: '0.65rem', borderBottom: '1px solid #D7E3DE', background: '#FFF', fontSize: '0.76rem' },
  currentPurchaseSummary: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.5rem' },
  dataMetric: { display: 'grid', gap: '0.25rem', minWidth: 0, padding: '0.65rem 0.7rem', border: '1px solid #D7E3DE', borderRadius: '0.55rem', background: '#EEF5F2', color: '#173F43' },
  purchasePeriodSummary: { gridColumn: '1 / -1' },
  infoBanner: { padding: '0.7rem', border: '1px solid #BED8CD', borderRadius: '0.55rem', background: '#EAF5EF', color: '#24634E', fontSize: '0.76rem' },
  warningBanner: { display: 'grid', gap: '0.15rem', padding: '0.7rem', border: '1px solid #E5C88B', borderRadius: '0.55rem', background: '#FFF4D8', color: '#744A16', fontSize: '0.76rem' },
  confirmationModalCard: { width: 'min(560px, 100%)', display: 'grid', gap: '0.85rem', padding: '1.15rem', border: '2px solid #7EA8A1', borderRadius: '0.9rem', background: '#FFFDF9', boxShadow: '0 26px 80px rgba(4, 31, 34, 0.42)' },
  destructiveModalCard: { borderColor: '#B94037' },
  lifecycleTarget: { display: 'grid', gap: '0.15rem', padding: '0.7rem', border: '1px solid #D7E3DE', borderRadius: '0.55rem', background: '#EEF5F2', color: '#173F43', fontSize: '0.8rem' },
  confirmationText: { margin: 0, color: '#627477', fontSize: '0.86rem', lineHeight: 1.5 },
  reductionImpactGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.5rem' },
  reductionImpactMetric: { display: 'grid', gap: '0.25rem', alignContent: 'start', padding: '0.7rem', border: '1px solid #D7E3DE', borderRadius: '0.55rem', background: '#EEF5F2', color: '#173F43' },
  confirmationWarning: { display: 'grid', gap: '0.3rem', padding: '0.75rem', border: '1px solid #E5C88B', borderRadius: '0.55rem', background: '#FFF4D8', color: '#744A16', fontSize: '0.78rem', lineHeight: 1.4 },
  destructiveButton: { border: '1px solid #A9362F', borderRadius: '0.55rem', padding: '0.58rem 0.72rem', background: '#B94037', color: '#FFF', fontWeight: 750, cursor: 'pointer' },
  issuedResult: { display: 'grid', gap: '0.75rem' },
  successBanner: { display: 'grid', gap: '0.15rem', padding: '0.7rem', borderRadius: '0.55rem', background: '#EAF5EF', color: '#24634E' },
  oneTimeKeyWarning: { display: 'grid', gap: '0.15rem', padding: '0.7rem', border: '1px solid #E5C88B', borderRadius: '0.55rem', background: '#FFF4D8', color: '#744A16' },
  activationKeyRow: { display: 'flex', gap: '0.5rem', alignItems: 'stretch' },
  activationKey: { minWidth: 0, flex: 1, overflowWrap: 'anywhere', padding: '0.72rem', border: '1px solid #B9CFCB', borderRadius: '0.5rem', background: '#F2F8F6', color: '#073F46', fontSize: '0.82rem', userSelect: 'all' },
  copyButton: { flex: '0 0 auto', border: '1px solid #0E5055', borderRadius: '0.5rem', padding: '0.6rem 0.8rem', background: '#0E5055', color: '#FFF', fontWeight: 750, cursor: 'pointer' },
  issuedSummary: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.5rem' }
};

export default PlatformSupportConsole;
